import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import "./ManagementsGeneralList.scss";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useContext, useMemo, useState } from "react";
import TotalEnergiesContext from "../../../core/store/context/TotalEnergiesContext";
import { ManagementTypeComponent } from "../../../core/models/managements/managements.model";
import { getAgentManagementUrl } from "../../../utils/agentData/agentData.utils";
import { AGENT_MANAGEMENT_SERVICE_CONSTANTS } from "../../../core/constants/agentManagements.constants";

const ManagementsGeneralList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isAgent } = useContext(TotalEnergiesContext);

  const [searchValue, setSearchValue] = useState<string>("");

  const managementsTypes: ManagementTypeComponent[] = useMemo(
    () => [
      {
        id: "managementsUpdatePowers",
        name: "Modificacion de potencias",
        icon: "t-icon-electricite-big",
        handleClick: () =>
          navigate(process.env.REACT_APP_MANAGEMENT_UPDATE_POWERS!),
      },
      {
        id: "managementsUpdateGasFlow",
        name: "Modificación de caudal diario (Qd)",
        icon: "t-icon-gaz-big",
        handleClick: () =>
          navigate(process.env.REACT_APP_MANAGEMENT_UPDATE_GAS_FLOW!),
      },
      {
        id: "managementsUpdateOwnership",
        name: "Cambio de titular",
        icon: "t-icon-building",
        handleClick: () =>
          navigate(process.env.REACT_APP_MANAGEMENT_OWNERSHIP_CHANGE!),
      },
      {
        id: "managementsUpdateDirectDebit",
        name: "Cambio de domiciliación bancaria",
        icon: "t-icon-Bank",
        handleClick: () =>
          navigate(process.env.REACT_APP_MANAGEMENT_UPDATE_BANK_DOMICILIATION!),
      },
      {
        id: "managementsUpdateContact",
        name: "Actualizar datos de contacto",
        icon: "t-icon-AddressBook",
        handleClick: () =>
          navigate(process.env.REACT_APP_MANAGEMENT_UPDATE_CONTACT!),
      },
      {
        id: "managementsUpdateCorrespondenceAddress",
        name: "Actualizar dirección de correspondencia",
        icon: "t-icon-home",
        handleClick: () =>
          navigate(
            process.env.REACT_APP_MANAGEMENT_UPDATE_CORRESPONDENCE_ADDRESS!,
          ),
      },
      {
        id: "managementsClaims",
        name: "Reclamaciones",
        icon: "t-icon-warning",
        handleClick: () => navigate(process.env.REACT_APP_MANAGEMENT_CLAIMS!),
      },
      {
        id: "managementsDismissalActivity",
        name: "Baja por cese de actividad",
        icon: "t-icon-power-off",
        handleClick: () =>
          navigate(process.env.REACT_APP_MANAGEMENT_CONTRACT_END!),
      },
      ...(isAgent
        ? Object.entries(
            AGENT_MANAGEMENT_SERVICE_CONSTANTS,
          ).map<ManagementTypeComponent>(([key, agentManagement]) => ({
            id: `agentManagement-${key}`,
            icon: agentManagement.iconClassName || "t-icon-info",
            name: agentManagement.titleKey,
            handleClick: () =>
              navigate(
                getAgentManagementUrl(
                  key as keyof typeof AGENT_MANAGEMENT_SERVICE_CONSTANTS,
                ),
              ),
          }))
        : []),
    ],
    [navigate, isAgent],
  );

  const managementsTypesFiltered =
    searchValue.length > 3
      ? managementsTypes.filter((management) =>
          t(`Gestiones.${management.name}`)
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .includes(
              searchValue
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, ""),
            ),
        )
      : managementsTypes;

  return (
    <Box
      component="section"
      className="last-managements"
      sx={{ paddingTop: { xs: "3.5rem", md: "5rem" } }}
      paddingBottom={{ xs: "2.5rem", md: "5rem" }}
    >
      <Typography component="h3" variant="h3" sx={{ mb: "1.5rem" }}>
        {t("Gestiones.¿Qué gestión desea realizar?")}
      </Typography>
      <form className="last-managements__search">
        <FormControl
          variant={"outlined"}
          sx={{
            width: { lg: "40%", md: "60%", xs: "100%" },
            mb: { md: "2.5rem", xs: "1.5rem" },
          }}
        >
          <OutlinedInput
            placeholder={t("Gestiones.Buscar gestión")}
            value={searchValue}
            className="form-control"
            startAdornment={
              <InputAdornment position="start" className="search-icon">
                <span className="t-icon-search" aria-hidden="true"></span>
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                {!!searchValue.length && (
                  <IconButton size="small" onClick={() => setSearchValue("")}>
                    <span className="t-icon-close" />
                  </IconButton>
                )}
              </InputAdornment>
            }
            onChange={(event) => {
              setSearchValue(event.target.value);
            }}
          />
        </FormControl>
      </form>
      <Grid container spacing={"1.25rem"}>
        {managementsTypesFiltered.map(({ id, name, icon, handleClick }) => (
          <Grid item key={id} xs={12} md={4}>
            <Stack
              boxShadow="0px 3px 5px 0px rgba(55, 70, 73, 0.20)"
              bgcolor="background.default"
              paddingY="2rem"
              paddingX="1.25rem"
              borderRadius="16px"
              direction={{ xs: "row", md: "column" }}
              gap={{ xs: "1.5rem", md: "2rem" }}
              key={icon}
              minHeight={{ xs: "55px", md: "130px" }}
              onClick={() => handleClick && handleClick()}
              sx={{ cursor: "pointer" }}
              data-testid={id}
            >
              <Typography
                component="p"
                variant="h1"
                sx={{ color: "primary.main" }}
              >
                <span className={icon} />
              </Typography>
              <Stack>
                <Typography
                  component="h3"
                  variant="subtitle1"
                  sx={{ color: "grey.700" }}
                >
                  {t(`Gestiones.${name}`)}
                </Typography>
              </Stack>
            </Stack>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ManagementsGeneralList;
